import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, Crown, ChevronDown, ChevronRight, Plus, X, Search, MoreVertical, Edit2, Trash2, Users as UsersIcon, Network, List as ListIcon, Maximize2, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { fetchFamilyTreePersons, createFamilyTreePerson, updateFamilyTreePerson, deleteFamilyTreePerson } from '../services/familyTreeApi';
import { getFamilyInitials } from '../utils/userUtils';
import Premium from './Premium';
import UserAvatar from './UserAvatar';

const FamilyTree = () => {
    const navigate = useNavigate();
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [viewMode, setViewMode] = useState('canvas'); // 'canvas' or 'list'
    const [showAddModal, setShowAddModal] = useState(false);
    const [showRelationModal, setShowRelationModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingPerson, setEditingPerson] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [selectedPerson, setSelectedPerson] = useState(null); // For adding relative
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    
    // Form states
    const [name, setName] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [dateOfDeath, setDateOfDeath] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [photo, setPhoto] = useState(null);
    const [location, setLocation] = useState('');
    const [otherDetails, setOtherDetails] = useState('');
    const [relations, setRelations] = useState([{ personId: '', relationType: '' }]);
    
    // Relation modal states
    const [relationType, setRelationType] = useState('');
    
    // Family tree data
    const [familyMembers, setFamilyMembers] = useState([]);
    
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    const relationTypes = [
        'Parent',
        'Child',
        'Spouse',
        'Sibling',
        'Grandparent',
        'Grandchild',
        'Uncle/Aunt',
        'Nephew/Niece',
        'Cousin',
        'Other'
    ];

    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load family tree persons when family changes
    useEffect(() => {
        const loadTreeData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchFamilyTreePersons(selectedFamily.id);
                const members = response.items || [];
                
                // Recalculate positions if they don't exist
                const hasPositions = members.every(m => m.position && m.position.x !== undefined);
                if (!hasPositions && members.length > 0) {
                    const positions = calculateTreeLayout(members);
                    const membersWithPositions = members.map(p => ({
                        ...p,
                        position: positions[p.id] || { x: 100, y: 100 }
                    }));
                    setFamilyMembers(membersWithPositions);
                } else {
                    setFamilyMembers(members);
                }
            } catch (error) {
                console.error('Error loading family members:', error);
            }
        };
        loadTreeData();
    }, [selectedFamily]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showAddModal || showRelationModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showAddModal, showRelationModal]);

    useEffect(() => {
        // Save family members to localStorage whenever they change
        localStorage.setItem('familyTreeMembers', JSON.stringify(familyMembers));
    }, [familyMembers]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id.toString());
        setShowFamilyDropdown(false);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhoto(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const calculateTreeLayout = (members) => {
        // Build parent-child and spouse relationships
        const childrenMap = {};
        const parentsMap = {};
        const spouseMap = {};
        
        members.forEach(person => {
            person.relations?.forEach(rel => {
                if (rel.relationType === 'Parent') {
                    if (!childrenMap[rel.personId]) childrenMap[rel.personId] = [];
                    childrenMap[rel.personId].push(person.id);
                    parentsMap[person.id] = parentsMap[person.id] || [];
                    parentsMap[person.id].push(parseInt(rel.personId));
                } else if (rel.relationType === 'Child') {
                    if (!childrenMap[person.id]) childrenMap[person.id] = [];
                    childrenMap[person.id].push(parseInt(rel.personId));
                    parentsMap[rel.personId] = parentsMap[rel.personId] || [];
                    parentsMap[rel.personId].push(person.id);
                } else if (rel.relationType === 'Spouse') {
                    if (!spouseMap[person.id]) spouseMap[person.id] = [];
                    spouseMap[person.id].push(parseInt(rel.personId));
                    if (!spouseMap[rel.personId]) spouseMap[rel.personId] = [];
                    spouseMap[rel.personId].push(person.id);
                }
            });
        });

        // Find root nodes (people with no parents)
        const roots = members.filter(p => !parentsMap[p.id] || parentsMap[p.id].length === 0);
        
        const positioned = new Set();
        const levels = {};
        const HORIZONTAL_SPACING = 180;
        const SPOUSE_SPACING = 150; // Spacing between spouses
        const VERTICAL_SPACING = 200;
        
        // BFS to assign levels
        const assignLevels = (personId, level) => {
            if (positioned.has(personId)) return;
            positioned.add(personId);
            
            if (!levels[level]) levels[level] = [];
            levels[level].push(personId);
            
            // Add spouses at the same level
            const spouses = spouseMap[personId] || [];
            spouses.forEach(spouseId => {
                if (!positioned.has(spouseId)) {
                    positioned.add(spouseId);
                    levels[level].push(spouseId);
                }
            });
            
            // Add children at next level
            const children = childrenMap[personId] || [];
            children.forEach(childId => {
                if (!positioned.has(childId)) {
                    assignLevels(childId, level + 1);
                }
            });
        };
        
        // Start from roots
        if (roots.length > 0) {
            roots.forEach((root, index) => {
                assignLevels(root.id, 0);
            });
        } else if (members.length > 0) {
            // If no clear root, start with first person
            assignLevels(members[0].id, 0);
        }
        
        // Position nodes
        const positions = {};
        Object.keys(levels).forEach(level => {
            const nodesInLevel = levels[level];
            const startX = 50;
            let currentX = startX;
            
            // Group spouses together
            const processedInLevel = new Set();
            let index = 0;
            
            nodesInLevel.forEach((personId) => {
                if (processedInLevel.has(personId)) return;
                
                processedInLevel.add(personId);
                positions[personId] = {
                    x: currentX,
                    y: 50 + (parseInt(level) * VERTICAL_SPACING)
                };
                
                // Position spouses next to this person
                const spouses = spouseMap[personId] || [];
                let spouseOffset = SPOUSE_SPACING;
                spouses.forEach(spouseId => {
                    if (!processedInLevel.has(spouseId) && nodesInLevel.includes(spouseId)) {
                        processedInLevel.add(spouseId);
                        positions[spouseId] = {
                            x: currentX + spouseOffset,
                            y: 50 + (parseInt(level) * VERTICAL_SPACING)
                        };
                        spouseOffset += SPOUSE_SPACING;
                    }
                });
                
                currentX += Math.max(HORIZONTAL_SPACING, spouseOffset);
                index++;
            });
        });
        
        return positions;
    };

    const handleAddPerson = () => {
        if (!name) {
            alert('Please enter a name');
            return;
        }

        const newPerson = {
            id: Date.now(),
            name,
            dateOfBirth,
            dateOfDeath,
            email,
            phone,
            photo,
            location,
            otherDetails,
            relations: relations.filter(r => r.personId && r.relationType),
            addedAt: new Date().toISOString()
        };

        let updatedMembers;
        if (editingPerson) {
            updatedMembers = familyMembers.map(p => 
                p.id === editingPerson.id ? { ...editingPerson, ...newPerson, id: editingPerson.id } : p
            );
        } else {
            updatedMembers = [...familyMembers, newPerson];
        }
        
        // Calculate positions for all members
        const positions = calculateTreeLayout(updatedMembers);
        const membersWithPositions = updatedMembers.map(p => ({
            ...p,
            position: positions[p.id] || { x: 100, y: 100 }
        }));
        
        setFamilyMembers(membersWithPositions);
        resetForm();
    };

    const handleAddRelative = () => {
        if (!relationType) {
            alert('Please select a relation type');
            return;
        }

        // Pre-fill the relation
        setRelations([{ personId: selectedPerson.id, relationType }]);
        setShowRelationModal(false);
        setShowAddModal(true);
    };

    const handleEditPerson = (person) => {
        setEditingPerson(person);
        setName(person.name);
        setDateOfBirth(person.dateOfBirth || '');
        setDateOfDeath(person.dateOfDeath || '');
        setEmail(person.email || '');
        setPhone(person.phone || '');
        setPhoto(person.photo || null);
        setLocation(person.location || '');
        setOtherDetails(person.otherDetails || '');
        setRelations(person.relations && person.relations.length > 0 
            ? person.relations 
            : [{ personId: '', relationType: '' }]);
        setShowAddModal(true);
        setOpenMenuId(null);
    };

    const handleDeletePerson = (id) => {
        if (window.confirm('Are you sure you want to delete this person?')) {
            setFamilyMembers(familyMembers.filter(p => p.id !== id));
        }
        setOpenMenuId(null);
    };

    const resetForm = () => {
        setName('');
        setDateOfBirth('');
        setDateOfDeath('');
        setEmail('');
        setPhone('');
        setPhoto(null);
        setLocation('');
        setOtherDetails('');
        setRelations([{ personId: '', relationType: '' }]);
        setEditingPerson(null);
        setSelectedPerson(null);
        setShowAddModal(false);
        
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const addRelation = () => {
        setRelations([...relations, { personId: '', relationType: '' }]);
    };

    const removeRelation = (index) => {
        if (relations.length > 1) {
            setRelations(relations.filter((_, i) => i !== index));
        }
    };

    const updateRelation = (index, field, value) => {
        const newRelations = [...relations];
        newRelations[index][field] = value;
        setRelations(newRelations);
    };

    const getFilteredMembers = () => {
        let filtered = [...familyMembers];

        if (searchQuery) {
            filtered = filtered.filter(m => 
                m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                m.location?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return filtered;
    };

    const getRelationName = (personId) => {
        const person = familyMembers.find(p => p.id === parseInt(personId));
        return person ? person.name : 'Unknown';
    };

    // Canvas interaction handlers
    const handleCanvasMouseDown = (e) => {
        // Only start dragging if clicking on the canvas background, not on nodes
        if (e.target.classList.contains('canvas-background') || e.target.tagName === 'svg' || e.target === canvasRef.current) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
            e.preventDefault();
        }
    };

    const handleCanvasMouseMove = (e) => {
        if (isDragging) {
            setPan({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
            e.preventDefault();
        }
    };

    const handleCanvasMouseUp = () => {
        setIsDragging(false);
    };

    const handleAddParent = (person, e) => {
        e.stopPropagation();
        setSelectedPerson(person);
        setRelationType('Child'); // Current person is child of new parent
        setRelations([{ personId: person.id, relationType: 'Child' }]);
        setShowAddModal(true);
    };

    const handleAddChild = (person, e) => {
        e.stopPropagation();
        setSelectedPerson(person);
        setRelationType('Parent'); // Current person is parent of new child
        setRelations([{ personId: person.id, relationType: 'Parent' }]);
        setShowAddModal(true);
    };

    const handleAddSpouse = (person, e) => {
        e.stopPropagation();
        setSelectedPerson(person);
        setRelationType('Spouse'); // Current person is spouse of new person
        setRelations([{ personId: person.id, relationType: 'Spouse' }]);
        setShowAddModal(true);
    };

    const getChildrenIds = (personId) => {
        const children = [];
        familyMembers.forEach(member => {
            member.relations?.forEach(rel => {
                if (rel.relationType === 'Parent' && parseInt(rel.personId) === personId) {
                    children.push(member.id);
                } else if (rel.relationType === 'Child' && member.id === personId) {
                    children.push(parseInt(rel.personId));
                }
            });
        });
        return children;
    };

    const getSpouseIds = (personId) => {
        const spouses = [];
        familyMembers.forEach(member => {
            member.relations?.forEach(rel => {
                if (rel.relationType === 'Spouse' && parseInt(rel.personId) === personId) {
                    spouses.push(member.id);
                } else if (rel.relationType === 'Spouse' && member.id === personId) {
                    spouses.push(parseInt(rel.personId));
                }
            });
        });
        return spouses;
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 0.1, 2));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 0.1, 0.5));
    };

    const handleResetView = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img 
                                                src={selectedFamily.photo} 
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showFamilyDropdown && (
                                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        {families.length > 0 ? (
                                            <>
                                                {families.map((family) => (
                                                    <button
                                                        key={family.id}
                                                        onClick={() => {
                                                            setSelectedFamily(family);
                                                            localStorage.setItem('selectedFamilyId', family.id.toString());
                                                            setShowFamilyDropdown(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${
                                                            selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                        }`}
                                                    >
                                                        {family.photo ? (
                                                            <img 
                                                                src={family.photo} 
                                                                alt={family.name}
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md">
                                                                {getFamilyInitials(family.name)}
                                                            </div>
                                                        )}
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-gray-800">{family.name}</p>
                                                            {family.tagline && (
                                                                <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                                <div className="border-t border-gray-200 my-2"></div>
                                            </>
                                        ) : (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                No families yet
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowFamilyDropdown(false);
                                                navigate('/famlis');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Add Family</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Premium Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPremiumModal(true)}
                                    className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <span className="hidden sm:inline">Go Premium</span>
                                        <span className="sm:hidden">Premium</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </button>
                                
                                {/* Golden Crown Overlay */}
                                <div className="absolute -top-1 -right-1 transform rotate-12">
                                    <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            </div>
                            
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    <UserAvatar size="medium" />
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">User Account</p>
                                            <p className="text-xs text-gray-500">Manage your profile</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-full px-4 py-8">
                {/* Breadcrumb */}
                <div className="max-w-7xl mx-auto mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Family Tree</span>
                </div>

                {/* Header */}
                <div className="max-w-7xl mx-auto mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-3xl font-bold text-gray-900">Family Tree</h2>
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        {/* Search */}
                        <div className="relative flex-1 sm:flex-none">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search members..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent w-full sm:w-64"
                            />
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('canvas')}
                                className={`p-2 rounded ${viewMode === 'canvas' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="Canvas view"
                            >
                                <Network className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="List view"
                            >
                                <ListIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Add Button */}
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                            <Plus className="w-5 h-5" />
                            Add Person
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                {viewMode === 'list' ? (
                    /* List View */
                    <div className="max-w-7xl mx-auto">
                        {getFilteredMembers().length === 0 ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">No family members yet. Click "Add Person" to get started!</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {getFilteredMembers().map((person) => (
                                    <div key={person.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-start gap-4 flex-1">
                                                {person.photo ? (
                                                    <img
                                                        src={person.photo}
                                                        alt={person.name}
                                                        className="w-16 h-16 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                                                        {person.name.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-lg font-bold text-gray-900 truncate">{person.name}</h3>
                                                    {person.dateOfBirth && (
                                                        <p className="text-sm text-gray-600">
                                                            Born: {new Date(person.dateOfBirth).toLocaleDateString()}
                                                        </p>
                                                    )}
                                                    {person.location && (
                                                        <p className="text-sm text-gray-600 truncate">{person.location}</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Menu */}
                                            <div className="relative flex-shrink-0">
                                                <button
                                                    onClick={() => setOpenMenuId(openMenuId === person.id ? null : person.id)}
                                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                >
                                                    <MoreVertical className="w-5 h-5 text-gray-600" />
                                                </button>

                                                {openMenuId === person.id && (
                                                    <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                        <button
                                                            onClick={() => handleEditPerson(person)}
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                            <span>Edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedPerson(person);
                                                                setShowRelationModal(true);
                                                                setOpenMenuId(null);
                                                            }}
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                        >
                                                            <Plus className="w-4 h-4" />
                                                            <span>Add Relative</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeletePerson(person.id)}
                                                            className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            <span>Delete</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Relations */}
                                        {person.relations && person.relations.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                                <p className="text-xs text-gray-500 mb-2">Relations:</p>
                                                <div className="space-y-1">
                                                    {person.relations.map((rel, index) => (
                                                        <div key={index} className="text-sm text-gray-700">
                                                            <span className="font-medium">{rel.relationType}:</span> {getRelationName(rel.personId)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Add Relative Button */}
                                        <button
                                            onClick={() => {
                                                setSelectedPerson(person);
                                                setShowRelationModal(true);
                                            }}
                                            className="mt-4 w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-blue hover:text-primary-blue transition-colors text-sm font-medium"
                                        >
                                            + Add Relative
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    /* Canvas View */
                    <div className="relative w-full" style={{ height: 'calc(100vh - 250px)' }}>
                        {/* Canvas Controls */}
                        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                            <button
                                onClick={handleZoomIn}
                                className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                                title="Zoom In"
                            >
                                <ZoomIn className="w-5 h-5 text-gray-700" />
                            </button>
                            <button
                                onClick={handleZoomOut}
                                className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                                title="Zoom Out"
                            >
                                <ZoomOut className="w-5 h-5 text-gray-700" />
                            </button>
                            <button
                                onClick={handleResetView}
                                className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                                title="Reset View"
                            >
                                <Maximize2 className="w-5 h-5 text-gray-700" />
                            </button>
                        </div>

                        {/* Instructions */}
                        <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-md p-3 text-sm text-gray-600 max-w-xs">
                            <div className="flex items-center gap-2 mb-2">
                                <Move className="w-4 h-4" />
                                <span className="font-medium">Drag canvas to pan</span>
                            </div>
                            <div className="space-y-1 text-xs">
                                <p>• Click person to edit details</p>
                                <p>• <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span> Green + = Add Parent</p>
                                <p>• <span className="inline-block w-3 h-3 bg-blue-500 rounded-full"></span> Blue + = Add Child</p>
                                <p>• <span className="inline-block w-3 h-3 bg-pink-500 rounded-full"></span> Pink + = Add Spouse</p>
                            </div>
                        </div>

                        {/* Canvas */}
                        <div
                            ref={canvasRef}
                            className="w-full h-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-inner overflow-hidden relative canvas-background"
                            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                            onMouseDown={handleCanvasMouseDown}
                            onMouseMove={handleCanvasMouseMove}
                            onMouseUp={handleCanvasMouseUp}
                            onMouseLeave={handleCanvasMouseUp}
                        >
                            {familyMembers.length === 0 ? (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="text-center pointer-events-auto">
                                        <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-500 mb-4">No family members yet</p>
                                        <button
                                            onClick={() => setShowAddModal(true)}
                                            className="px-6 py-3 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                        >
                                            Add First Person
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="canvas-background"
                                    style={{
                                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                        transformOrigin: 'center',
                                        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                                        width: '2000px',
                                        height: '2000px',
                                        position: 'relative'
                                    }}
                                >
                                    {/* Draw connection lines - horizontal and vertical only */}
                                    <svg className="absolute inset-0 w-full h-full pointer-events-none canvas-background" style={{ zIndex: 0 }}>
                                        {/* Draw spouse connections (horizontal lines) */}
                                        {familyMembers.map(person => {
                                            const spouseIds = getSpouseIds(person.id);
                                            return spouseIds.map(spouseId => {
                                                // Only draw once per spouse pair
                                                if (person.id >= spouseId) return null;
                                                
                                                const spouse = familyMembers.find(p => p.id === spouseId);
                                                if (!spouse) return null;
                                                
                                                const person1X = person.position.x + 120;
                                                const person1Y = person.position.y + 60;
                                                const person2X = spouse.position.x;
                                                const person2Y = spouse.position.y + 60;
                                                
                                                return (
                                                    <line
                                                        key={`spouse-${person.id}-${spouseId}`}
                                                        x1={person1X}
                                                        y1={person1Y}
                                                        x2={person2X}
                                                        y2={person2Y}
                                                        stroke="#f472b6"
                                                        strokeWidth="2"
                                                    />
                                                );
                                            });
                                        })}
                                        
                                        {/* Draw parent-child connections */}
                                        {familyMembers.map(person => {
                                            const childrenIds = getChildrenIds(person.id);
                                            return childrenIds.map(childId => {
                                                const child = familyMembers.find(p => p.id === childId);
                                                if (!child) return null;
                                                
                                                const parentX = person.position.x + 60;
                                                const parentY = person.position.y + 120; // Bottom of parent card
                                                const childX = child.position.x + 60;
                                                const childY = child.position.y; // Top of child card
                                                
                                                const midY = (parentY + childY) / 2;
                                                
                                                return (
                                                    <g key={`child-${person.id}-${childId}`}>
                                                        {/* Vertical line from parent */}
                                                        <line
                                                            x1={parentX}
                                                            y1={parentY}
                                                            x2={parentX}
                                                            y2={midY}
                                                            stroke="#94a3b8"
                                                            strokeWidth="2"
                                                        />
                                                        {/* Horizontal line */}
                                                        <line
                                                            x1={parentX}
                                                            y1={midY}
                                                            x2={childX}
                                                            y2={midY}
                                                            stroke="#94a3b8"
                                                            strokeWidth="2"
                                                        />
                                                        {/* Vertical line to child */}
                                                        <line
                                                            x1={childX}
                                                            y1={midY}
                                                            x2={childX}
                                                            y2={childY}
                                                            stroke="#94a3b8"
                                                            strokeWidth="2"
                                                        />
                                                    </g>
                                                );
                                            });
                                        })}
                                    </svg>

                                    {/* Draw person nodes */}
                                    {familyMembers.map((person) => (
                                        <div
                                            key={person.id}
                                            className="absolute"
                                            style={{
                                                left: person.position.x,
                                                top: person.position.y,
                                                width: '120px',
                                                zIndex: 10
                                            }}
                                        >
                                            {/* Add Parent Button */}
                                            <button
                                                onClick={(e) => handleAddParent(person, e)}
                                                className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors z-20"
                                                title="Add Parent"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>

                                            {/* Add Spouse Button (Left) */}
                                            <button
                                                onClick={(e) => handleAddSpouse(person, e)}
                                                className="absolute left-0 top-1/2 transform -translate-x-8 -translate-y-1/2 w-6 h-6 bg-pink-500 hover:bg-pink-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors z-20"
                                                title="Add Spouse"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>

                                            {/* Person Card */}
                                            <div
                                                className="bg-white rounded-xl shadow-lg p-4 cursor-pointer hover:shadow-xl transition-shadow"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditPerson(person);
                                                }}
                                            >
                                                <div className="text-center">
                                                    {person.photo ? (
                                                        <img
                                                            src={person.photo}
                                                            alt={person.name}
                                                            className="w-16 h-16 rounded-full object-cover mx-auto mb-2"
                                                        />
                                                    ) : (
                                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold mx-auto mb-2">
                                                            {person.name.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <p className="text-sm font-semibold text-gray-900 truncate">{person.name}</p>
                                                    {person.dateOfBirth && (
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(person.dateOfBirth).getFullYear()}
                                                            {person.dateOfDeath && ` - ${new Date(person.dateOfDeath).getFullYear()}`}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Add Spouse Button (Right) */}
                                            <button
                                                onClick={(e) => handleAddSpouse(person, e)}
                                                className="absolute right-0 top-1/2 transform translate-x-8 -translate-y-1/2 w-6 h-6 bg-pink-500 hover:bg-pink-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors z-20"
                                                title="Add Spouse"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>

                                            {/* Add Child Button */}
                                            <button
                                                onClick={(e) => handleAddChild(person, e)}
                                                className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors z-20"
                                                title="Add Child"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Relation Selection Modal */}
            {showRelationModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">
                                Add Relative to {selectedPerson?.name}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowRelationModal(false);
                                    setRelationType('');
                                    setSelectedPerson(null);
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Relation Type *
                                </label>
                                <select
                                    value={relationType}
                                    onChange={(e) => setRelationType(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                >
                                    <option value="">Choose relation...</option>
                                    {relationTypes.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowRelationModal(false);
                                        setRelationType('');
                                        setSelectedPerson(null);
                                    }}
                                    className="flex-1 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddRelative}
                                    disabled={!relationType}
                                    className="flex-1 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Person Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingPerson ? 'Edit Person' : selectedPerson && relationType ? 
                                    (relationType === 'Child' ? `Add Parent of ${selectedPerson.name}` : 
                                     relationType === 'Parent' ? `Add Child of ${selectedPerson.name}` : 
                                     relationType === 'Spouse' ? `Add Spouse of ${selectedPerson.name}` :
                                     `Add ${relationType} of ${selectedPerson.name}`) 
                                    : 'Add Person'}
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Photo */}
                            <div className="text-center">
                                {photo ? (
                                    <div className="relative inline-block">
                                        <img
                                            src={photo}
                                            alt="Preview"
                                            className="w-32 h-32 rounded-full object-cover mx-auto"
                                        />
                                        <button
                                            onClick={() => setPhoto(null)}
                                            className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                                        <UsersIcon className="w-16 h-16 text-gray-400" />
                                    </div>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="mt-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    {photo ? 'Change Photo' : 'Upload Photo'}
                                </button>
                            </div>

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Full name"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Date of Birth and Death */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                                    <input
                                        type="date"
                                        value={dateOfBirth}
                                        onChange={(e) => setDateOfBirth(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Death (Optional)</label>
                                    <input
                                        type="date"
                                        value={dateOfDeath}
                                        onChange={(e) => setDateOfDeath(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                    />
                                </div>
                            </div>

                            {/* Email and Phone */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="email@example.com"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+1 234 567 8900"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                    />
                                </div>
                            </div>

                            {/* Location */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                <input
                                    type="text"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="City, Country"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Relations */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Relations</label>
                                    <button
                                        type="button"
                                        onClick={addRelation}
                                        className="text-sm text-primary-blue hover:text-blue-700 font-medium flex items-center gap-1"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Relation
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {relations.map((relation, index) => (
                                        <div key={index} className="p-3 border border-gray-200 rounded-lg">
                                            <div className="flex items-start gap-2">
                                                <div className="flex-1 grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">Person</label>
                                                        <select
                                                            value={relation.personId}
                                                            onChange={(e) => updateRelation(index, 'personId', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            disabled={selectedPerson && index === 0}
                                                        >
                                                            <option value="">Select person...</option>
                                                            {familyMembers
                                                                .filter(p => !editingPerson || p.id !== editingPerson.id)
                                                                .map(person => (
                                                                    <option key={person.id} value={person.id}>{person.name}</option>
                                                                ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">Relation Type</label>
                                                        <select
                                                            value={relation.relationType}
                                                            onChange={(e) => updateRelation(index, 'relationType', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            disabled={selectedPerson && index === 0}
                                                        >
                                                            <option value="">Select type...</option>
                                                            {relationTypes.map(type => (
                                                                <option key={type} value={type}>{type}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                {relations.length > 1 && !(selectedPerson && index === 0) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRelation(index)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Other Details */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Other Details</label>
                                <textarea
                                    value={otherDetails}
                                    onChange={(e) => setOtherDetails(e.target.value)}
                                    placeholder="Any additional information..."
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent resize-none"
                                />
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddPerson}
                                disabled={!name}
                                className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingPerson ? 'Update' : 'Add'} Person
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FamilyTree;

